import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useSiteRecoveryCalc } from '../../hooks/useCalculate'
import { useAnalytics } from '../../hooks/useAnalytics'
import type { SiteRecoveryRequest, SiteRecoveryInstanceGroup } from '../../api/types'
import { TrustBar } from '../shared/TrustBar'
import { FieldTooltip } from '../shared/FieldTooltip'
import { ShareButton } from '../shared/ShareButton'
import { DiscountModifier } from '../shared/DiscountModifier'

const VM_SKUS = ['D2s v3', 'D4s v3', 'D8s v3', 'D2s v5', 'D4s v5', 'B2s', 'B4ms']

const INSTANCE_LABELS: Record<string, string> = {
  azure_vm:       'Azure VM',
  on_premises_vm: 'On-premises VM (Hyper-V/VMware)',
  sql_server:     'SQL Server instance',
  sap_hana:       'SAP HANA instance',
}

const DEFAULT_INSTANCES: SiteRecoveryInstanceGroup[] = [
  { type: 'azure_vm', count: 5 },
]

const DEFAULT_INPUTS: Required<SiteRecoveryRequest> = {
  instances: DEFAULT_INSTANCES,
  replicaDiskSizeGB: 128,
  replicaRedundancy: 'lrs',
  replicationTransferGBPerMonth: 10,
  failoverTest: {
    enabled: true,
    frequencyPerYear: 2,
    durationHours: 4,
    vmSku: 'D2s v3',
  },
  azureHybridBenefit: false,
}

function fmt(n: number) {
  return n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function NumInput({
  label,
  value,
  onChange,
  min = 0,
  max,
  step = 1,
  hint,
}: {
  label: string
  value: number
  onChange: (v: number) => void
  min?: number
  max?: number
  step?: number
  hint?: string
}) {
  return (
    <div>
      <label className="block text-[13px] font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
        {label}
      </label>
      {hint && <p className="text-[11px] mb-1" style={{ color: 'var(--text-tertiary)' }}>{hint}</p>}
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(Math.max(min, Number(e.target.value)))}
        className="w-full rounded px-3 py-2 text-[13px] border"
        style={{
          backgroundColor: 'var(--surface-container)',
          borderColor: 'var(--outline-variant)',
          color: 'var(--text-primary)',
        }}
      />
    </div>
  )
}

export default function SiteRecoveryCalc() {
  const [inputs, setInputs] = useState<Required<SiteRecoveryRequest>>(DEFAULT_INPUTS)
  const [discount, setDiscount] = useState(0)
  const { data, isLoading, isError } = useSiteRecoveryCalc(inputs)
  const { trackCalculatorUsed } = useAnalytics()
  useEffect(() => {
    if (data?.totalMonthly) {
      trackCalculatorUsed({ calculator: 'site-recovery', result_gbp: data.totalMonthly })
    }
  }, [data?.totalMonthly])


  function updateInstances(idx: number, field: keyof SiteRecoveryInstanceGroup, value: string | number) {
    setInputs((prev) => {
      const next = prev.instances.map((g, i) =>
        i === idx ? { ...g, [field]: value } : g,
      )
      return { ...prev, instances: next }
    })
  }

  function addInstanceGroup() {
    setInputs((prev) => ({
      ...prev,
      instances: [
        ...prev.instances,
        { type: 'azure_vm' as const, count: 1 },
      ],
    }))
  }

  function removeInstanceGroup(idx: number) {
    setInputs((prev) => ({
      ...prev,
      instances: prev.instances.filter((_, i) => i !== idx),
    }))
  }

  function setFailover<K extends keyof typeof inputs.failoverTest>(
    key: K,
    value: typeof inputs.failoverTest[K],
  ) {
    setInputs((prev) => ({ ...prev, failoverTest: { ...prev.failoverTest, [key]: value } }))
  }

  const total = data?.totalMonthly ?? 0
  const discounted = discount > 0 ? total * (1 - discount / 100) : null

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* ── Left column: inputs ── */}
      <div className="flex flex-col gap-6">

        {/* Protected instances */}
        <section
          className="rounded-lg p-5"
          style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
        >
          <h2 className="text-[13px] font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--text-tertiary)' }}>
            Protected Instances
          </h2>

          {/* Free trial callout */}
          <div
            className="rounded p-3 mb-4 text-[12px] leading-relaxed"
            style={{ backgroundColor: 'var(--surface-container-low)', borderLeft: '3px solid #0078d4', color: 'var(--text-secondary)' }}
          >
            <p>
              <span className="font-medium">31-day free trial per instance.</span> Each newly protected instance has its first 31 days free. This covers initial replication setup — instance fees start from day 32. Replica storage and transfer charges begin immediately.
            </p>
          </div>

          <div className="flex flex-col gap-3">
            {inputs.instances.map((group, idx) => (
              <div
                key={idx}
                className="rounded p-3 flex flex-col gap-2"
                style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid var(--outline-variant)' }}
              >
                <div className="flex items-center justify-between">
                  <label className="text-[12px] font-medium" style={{ color: 'var(--text-secondary)' }}>
                    Instance type
                  </label>
                  {inputs.instances.length > 1 && (
                    <button
                      onClick={() => removeInstanceGroup(idx)}
                      className="text-[11px] hover:underline"
                      style={{ color: '#dc2626' }}
                    >
                      Remove
                    </button>
                  )}
                </div>
                <select
                  value={group.type}
                  onChange={(e) => updateInstances(idx, 'type', e.target.value)}
                  className="w-full rounded px-3 py-2 text-[13px] border"
                  style={{
                    backgroundColor: 'var(--surface-container)',
                    borderColor: 'var(--outline-variant)',
                    color: 'var(--text-primary)',
                  }}
                >
                  {Object.entries(INSTANCE_LABELS).map(([val, label]) => (
                    <option key={val} value={val}>{label}</option>
                  ))}
                </select>
                <div className="flex items-center gap-2">
                  <label className="text-[12px]" style={{ color: 'var(--text-secondary)' }}>Count</label>
                  <input
                    type="number"
                    value={group.count}
                    min={1}
                    max={1000}
                    onChange={(e) => updateInstances(idx, 'count', Math.max(1, Number(e.target.value)))}
                    className="w-24 rounded px-3 py-1.5 text-[13px] border"
                    style={{
                      backgroundColor: 'var(--surface-container)',
                      borderColor: 'var(--outline-variant)',
                      color: 'var(--text-primary)',
                    }}
                  />
                  <span className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                    × £18.87/instance/month
                  </span>
                </div>
              </div>
            ))}
            <button
              onClick={addInstanceGroup}
              className="text-[12px] font-medium hover:underline text-left"
              style={{ color: '#0078d4' }}
            >
              + Add another instance type
            </button>
          </div>

          <label className="flex items-center gap-3 cursor-pointer mt-4">
            <input
              type="checkbox"
              checked={inputs.azureHybridBenefit}
              onChange={(e) => setInputs((prev) => ({ ...prev, azureHybridBenefit: e.target.checked }))}
              className="w-4 h-4 rounded"
              style={{ accentColor: '#0078d4' }}
            />
            <span className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>
              Azure Hybrid Benefit (on-premises VMs — ~30% discount on licensing component)
            </span>
          </label>
        </section>

        {/* Replica storage */}
        <section
          className="rounded-lg p-5"
          style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
        >
          <h2 className="text-[13px] font-bold uppercase tracking-widest mb-4" style={{ color: 'var(--text-tertiary)' }}>
            Replica Storage
          </h2>
          <div className="flex flex-col gap-4">
            <NumInput
              label="Replica disk size (GB)"
              value={inputs.replicaDiskSizeGB}
              onChange={(v) => setInputs((prev) => ({ ...prev, replicaDiskSizeGB: v }))}
              min={1}
              max={65536}
              hint="ASR stores a replica of the source disk in the target region. Same size as your source disk."
            />
            <div>
              <label className="block text-[13px] font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                Replica redundancy <FieldTooltip content="LRS: single region. ZRS: across availability zones. GRS: geo-replicated to a paired region for disaster recovery." />
              </label>
              <p className="text-[11px] mb-1" style={{ color: 'var(--text-tertiary)' }}>
                LRS is most common for DR. GRS adds a second regional copy.
              </p>
              <select
                value={inputs.replicaRedundancy}
                onChange={(e) => setInputs((prev) => ({ ...prev, replicaRedundancy: e.target.value as 'lrs' | 'zrs' | 'grs' }))}
                className="w-full rounded px-3 py-2 text-[13px] border"
                style={{
                  backgroundColor: 'var(--surface-container)',
                  borderColor: 'var(--outline-variant)',
                  color: 'var(--text-primary)',
                }}
              >
                <option value="lrs">LRS — £0.0325/GB/month</option>
                <option value="zrs">ZRS — £0.0491/GB/month</option>
                <option value="grs">GRS — £0.0649/GB/month</option>
              </select>
              <p className="text-[11px] mt-1" style={{ color: 'var(--text-tertiary)' }}>
                Standard HDD (typical for DR). <Link to="/managed-disks" className="hover:underline" style={{ color: '#0078d4' }}>Refine with Managed Disks calculator →</Link>
              </p>
            </div>
          </div>
        </section>

        {/* Replication transfer */}
        <section
          className="rounded-lg p-5"
          style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
        >
          <h2 className="text-[13px] font-bold uppercase tracking-widest mb-4" style={{ color: 'var(--text-tertiary)' }}>
            Replication Transfer
          </h2>
          <NumInput
            label="Delta replication data (GB/month)"
            value={inputs.replicationTransferGBPerMonth}
            onChange={(v) => setInputs((prev) => ({ ...prev, replicationTransferGBPerMonth: v }))}
            min={0}
            step={1}
            hint="Changed data sent from source to replica region each month. Initial replication (full disk size) is a one-off cost billed separately."
          />
          <p className="text-[11px] mt-2" style={{ color: 'var(--text-tertiary)' }}>
            Rate: £0.015/GB (UK South → UK West, cross-region). <Link to="/bandwidth" className="hover:underline" style={{ color: '#0078d4' }}>Refine with Bandwidth calculator →</Link>
          </p>
        </section>

        {/* Failover test compute */}
        <section
          className="rounded-lg p-5"
          style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
        >
          <h2 className="text-[13px] font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--text-tertiary)' }}>
            Failover Test Compute
          </h2>
          <label className="flex items-center gap-3 cursor-pointer mb-4">
            <input
              type="checkbox"
              checked={inputs.failoverTest.enabled}
              onChange={(e) => setFailover('enabled', e.target.checked)}
              className="w-4 h-4 rounded"
              style={{ accentColor: '#0078d4' }}
            />
            <span className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>
              Include failover test costs
            </span>
          </label>

          {inputs.failoverTest.enabled && (
            <div className="flex flex-col gap-4">
              <NumInput
                label="Test failovers per year"
                value={inputs.failoverTest.frequencyPerYear}
                onChange={(v) => setFailover('frequencyPerYear', v)}
                min={0}
                max={52}
                hint="Microsoft recommends at least 2 DR drills/year. Quarterly (4/year) for critical workloads."
              />
              <NumInput
                label="Duration per test (hours)"
                value={inputs.failoverTest.durationHours}
                onChange={(v) => setFailover('durationHours', Math.max(1, v))}
                min={1}
                max={168}
                hint="VMs run at normal compute rates during the test. Shut them down promptly after."
              />
              <div>
                <label className="block text-[13px] font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
                  Failover VM size
                </label>
                <select
                  value={inputs.failoverTest.vmSku}
                  onChange={(e) => setFailover('vmSku', e.target.value)}
                  className="w-full rounded px-3 py-2 text-[13px] border"
                  style={{
                    backgroundColor: 'var(--surface-container)',
                    borderColor: 'var(--outline-variant)',
                    color: 'var(--text-primary)',
                  }}
                >
                  {VM_SKUS.map((sku) => <option key={sku} value={sku}>{sku}</option>)}
                </select>
                <p className="text-[11px] mt-1" style={{ color: 'var(--text-tertiary)' }}>
                  Approximate Linux rate. <Link to="/virtual-machines" className="hover:underline" style={{ color: '#0078d4' }}>Refine with VM calculator →</Link>
                </p>
              </div>
            </div>
          )}
        </section>

        <TrustBar />
      </div>

      {/* ── Right column: result ── */}
      <div className="flex flex-col gap-4">
        <div
          className="rounded-lg p-6"
          style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
        >
          <p className="text-[12px] uppercase tracking-widest font-bold mb-2" style={{ color: 'var(--text-tertiary)' }}>
            Monthly DR estimate (PAYG)
          </p>
          {isLoading && (
            <p className="text-[13px]" style={{ color: 'var(--text-tertiary)' }}>Calculating…</p>
          )}
          {isError && (
            <p className="text-[13px] text-red-500">Error — check inputs and try again.</p>
          )}
          {data && !isLoading && (
            <>
              <p className="text-3xl font-bold mb-1" style={{ color: '#0078d4' }}>
                £{fmt(discounted ?? total)}
                <span className="text-[14px] font-normal ml-1" style={{ color: 'var(--text-tertiary)' }}>/month</span>
              </p>
              {discounted !== null && (
                <p className="text-[12px] mb-3" style={{ color: 'var(--text-tertiary)' }}>
                  List price: £{fmt(total)}/month · {discount}% discount applied
                </p>
              )}

              {/* Full cost multiplier insight */}
              {data.fullCostMultiplier !== null && data.fullCostMultiplier > 1 && (
                <div
                  className="rounded p-3 mb-4 text-[12px]"
                  style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid var(--outline-variant)' }}
                >
                  <p className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
                    Your total DR cost is {data.fullCostMultiplier}× the per-instance fee alone
                  </p>
                  <p style={{ color: 'var(--text-secondary)' }}>
                    Instance fees account for only {data.instanceFeePercent}% of your monthly bill. Replica storage, replication transfer, and failover compute make up the rest — costs that don't appear on the Azure Site Recovery pricing page.
                  </p>
                </div>
              )}

              {/* Component breakdown */}
              <div
                className="rounded mt-2 p-4 text-[12px] flex flex-col gap-2"
                style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid var(--outline-variant)' }}
              >
                <p className="font-bold uppercase tracking-widest text-[11px] mb-1" style={{ color: 'var(--text-tertiary)' }}>Breakdown</p>

                {/* Instance fees */}
                {data.breakdown.instanceFees.byType.map((g) => (
                  <div key={g.type} className="flex justify-between">
                    <span style={{ color: 'var(--text-secondary)' }}>
                      {g.label} ({g.count} × £{fmt(g.ratePerInstance)})
                    </span>
                    <span className="font-mono font-medium" style={{ color: 'var(--text-primary)' }}>
                      £{fmt(g.total)}
                    </span>
                  </div>
                ))}
                {data.breakdown.instanceFees.hybridBenefitSaving > 0 && (
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--text-tertiary)' }}>Azure Hybrid Benefit saving</span>
                    <span className="font-mono" style={{ color: '#16a34a' }}>−£{fmt(data.breakdown.instanceFees.hybridBenefitSaving)}</span>
                  </div>
                )}

                {/* Replica storage */}
                <div className="flex justify-between">
                  <span style={{ color: 'var(--text-secondary)' }}>
                    Replica storage ({data.breakdown.replicaStorage.diskSizeGB} GB {data.breakdown.replicaStorage.redundancy} × £{fmt(data.breakdown.replicaStorage.ratePerGB)}/GB)
                  </span>
                  <span className="font-mono font-medium" style={{ color: 'var(--text-primary)' }}>
                    £{fmt(data.breakdown.replicaStorage.total)}
                  </span>
                </div>

                {/* Replication transfer */}
                <div className="flex justify-between">
                  <span style={{ color: 'var(--text-secondary)' }}>
                    Replication transfer ({data.breakdown.replicationTransfer.gbPerMonth} GB × £{fmt(data.breakdown.replicationTransfer.ratePerGB)}/GB)
                  </span>
                  <span className="font-mono font-medium" style={{ color: 'var(--text-primary)' }}>
                    £{fmt(data.breakdown.replicationTransfer.total)}
                  </span>
                </div>

                {/* Failover compute */}
                {data.breakdown.failoverCompute.enabled && (
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--text-secondary)' }}>
                      Failover tests ({data.breakdown.failoverCompute.testsPerYear}/yr × {data.breakdown.failoverCompute.hoursPerTest}h × {data.breakdown.failoverCompute.totalInstances} VMs ÷ 12)
                    </span>
                    <span className="font-mono font-medium" style={{ color: 'var(--text-primary)' }}>
                      £{fmt(data.breakdown.failoverCompute.monthlyEquivalent)}
                    </span>
                  </div>
                )}

                <div
                  className="flex justify-between pt-2 mt-1 font-bold"
                  style={{ borderTop: '1px solid var(--outline-variant)', color: 'var(--text-primary)' }}
                >
                  <span>Total</span>
                  <span className="font-mono">£{fmt(discounted ?? total)}/month</span>
                </div>
              </div>

              {/* Cross-links */}
              <div className="mt-4 flex flex-col gap-2">
                <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>Refine your estimate</p>
                <Link to="/managed-disks" className="text-[12px] hover:underline" style={{ color: '#0078d4' }}>
                  → Managed Disks calculator (replica storage detail)
                </Link>
                <Link to="/bandwidth" className="text-[12px] hover:underline" style={{ color: '#0078d4' }}>
                  → Bandwidth calculator (replication transfer detail)
                </Link>
                <Link to="/virtual-machines" className="text-[12px] hover:underline" style={{ color: '#0078d4' }}>
                  → Virtual Machines calculator (failover compute detail)
                </Link>
                <Link to="/azure-backup" className="text-[12px] hover:underline" style={{ color: '#0078d4' }}>
                  → Azure Backup calculator (point-in-time restore — different service, complementary protection)
                </Link>
              </div>

              {/* Formula disclosure */}
              <details className="mt-4">
                <summary className="text-[12px] cursor-pointer" style={{ color: 'var(--text-tertiary)' }}>
                  Show formula
                </summary>
                <div
                  className="mt-2 rounded p-3 text-[11px] font-mono leading-relaxed"
                  style={{ backgroundColor: 'var(--surface-container-low)', color: 'var(--text-secondary)' }}
                >
                  {data.breakdown.instanceFees.byType.map((g) => (
                    <p key={g.type}>{g.count} {g.label} × £{fmt(g.ratePerInstance)} = £{fmt(g.total)}</p>
                  ))}
                  <p>Replica: {data.breakdown.replicaStorage.diskSizeGB} GB × £{fmt(data.breakdown.replicaStorage.ratePerGB)}/GB = £{fmt(data.breakdown.replicaStorage.total)}</p>
                  <p>Transfer: {data.breakdown.replicationTransfer.gbPerMonth} GB × £{fmt(data.breakdown.replicationTransfer.ratePerGB)}/GB = £{fmt(data.breakdown.replicationTransfer.total)}</p>
                  {data.breakdown.failoverCompute.enabled && (
                    <p>Failover: {data.breakdown.failoverCompute.testsPerYear} tests × {data.breakdown.failoverCompute.hoursPerTest}h × £{data.breakdown.failoverCompute.vmHourlyRate}/hr × {data.breakdown.failoverCompute.totalInstances} VMs ÷ 12 = £{fmt(data.breakdown.failoverCompute.monthlyEquivalent)}/month</p>
                  )}
                  <p className="mt-1 font-bold">Total: £{fmt(total)}/month</p>
                  <p className="mt-2 text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                    {data.pricingNote}
                  </p>
                </div>
              </details>

              <DiscountModifier discount={discount} onDiscountChange={setDiscount} paygTotal={total} />

              <div className="mt-4">
                <ShareButton inputs={inputs} result={data} service="site-recovery" />
              </div>

              <a href="https://azure.microsoft.com/en-gb/pricing/details/site-recovery/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[10px] font-mono mt-2" style={{ color: 'var(--text-tertiary)' }}>
                <span className="material-symbols-outlined text-[13px]">open_in_new</span>
                Verify on official Azure pricing page
              </a>

              <p className="text-[11px] mt-4" style={{ color: 'var(--text-tertiary)' }}>
                Prices verified {data.prices_last_updated ? new Date(data.prices_last_updated).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}. Estimates only — see disclaimer.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
